import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from '../../auth.service';
import { TabIdentityService } from '../../services/tab-identity.service';
import { RefineryPresenceService } from '../../services/refinery-presence.service';

// Scoped to the .NET API's own controllers that require the session cookie
// (currently /api/Config/, /api/Refineries/, and /api/IngestFiles/ — NOT the Flask engine, which
// lives at a different origin and also happens to have "/api/" in its own
// paths; see isConfigUrl()), for the same URL-string-only reason
// CredentialsInterceptor documents for /api/Account/: HTTP_INTERCEPTORS
// construct on the very first HttpClient call in the app's life
// (AppEnvService.load() during bootstrap), so nothing here may
// constructor-inject a service whose own construction depends on that same
// bootstrap load having finished. AuthService/RefineryPresenceService are
// resolved lazily via Injector.get() only once an actual response needs them.
//
// 1. Sends/receives the HttpOnly session cookie on every matched request —
//    both ConfigController and RefineriesController require a valid session
//    (each independently checks GetAuthenticatedSession()), so without this
//    every call would look unauthenticated even when the Analyst is logged
//    in. RefineriesController (export/import) is a concrete example of why
//    this list needs to be kept in sync: it copied ConfigController's own
//    auth pattern, but its calls 401'd on every attempt until this
//    interceptor was told about its route prefix too.
//
// 2. Attaches X-Tab-Id so the backend can tell apart two tabs of the SAME
//    logged-in user — the whole reason the Edit lock is keyed on
//    (user, tab) rather than by session/cookie alone (a cookie is shared by
//    every tab of the same browser profile). See TabIdentityService.
//    (RefineriesController doesn't use the lock today, but it's harmless to
//    attach everywhere this interceptor already applies.)
//
// 3. A 403 from a lock-enforced Config-API call means "you don't currently
//    hold the Edit lock for this refinery" (see CheckEditLockAsync
//    server-side) — NOT a dead session like a /api/Account/ 401 is.
//    Reacting to it here, rather than in every caller, means a stale/raced
//    UI can't silently believe a save succeeded: the tab is dropped to
//    Read-Only immediately and the error still surfaces normally to
//    whatever triggered the call.
//
// 4. A 401 here means the same thing it does on /api/Account/ — no valid
//    session at all — so it's handled the same way: treat the session as
//    gone and notify every tab.
//
// IMPORTANT: adding a new .NET controller that needs the session cookie
// means adding its route prefix to isConfigUrl() below — this deliberately
// does NOT match "/api/" generically. The Flask engine's own routes also
// contain "/api/" (e.g. /api/v1.0/refinery/...), and its CORS policy
// (flask-cors, default config) uses a wildcard origin with credentials
// support OFF — attaching withCredentials to a Flask call under a wildcard
// origin makes the browser reject the response outright as a CORS
// violation. A generic "/api/" match would silently break every Flask call.
@Injectable()
export class ConfigApiInterceptor implements HttpInterceptor {
  constructor(private injector: Injector) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isConfigUrl(request.url)) {
      return next.handle(request);
    }

    const tabIdentity = this.injector.get(TabIdentityService);
    const decoratedRequest = request.clone({
      withCredentials: true,
      setHeaders: { 'X-Tab-Id': tabIdentity.tabId }
    });

    return next.handle(decoratedRequest).pipe(
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse) {
          if (error.status === 401) {
            this.handleUnauthorized();
          } else if (error.status === 403 && this.isLockEnforcedUrl(request.url)) {
            this.handleLockViolation();
          }
        }
        return throwError(() => error);
      })
    );
  }

  private isConfigUrl(url: string): boolean {
    return url.includes('/api/Config/') || url.includes('/api/Refineries/') || url.includes('/api/IngestFiles/');
  }

  // Only the endpoints actually guarded by an Edit-lock check server-side
  // mean "you don't hold the lock" on a 403 — presence check/list-read
  // endpoints can't 403 for that reason, so this stays narrow rather than
  // treating every possible 403 as a lock loss.
  private isLockEnforcedUrl(url: string): boolean {
    return url.includes('/api/Config/upsertRefineryConfig') || url.includes('/api/Config/refineryConfig/');
  }

  private handleUnauthorized(): void {
    const authService = this.injector.get(AuthService);
    // Already known logged-out (e.g. a background call firing before the
    // Analyst ever logged in, or a second 401 arriving right after the
    // first) — nothing to do, and importantly nothing to re-broadcast.
    if (!authService.getLoginStatus()) {
      return;
    }
    void authService.logout();
  }

  private handleLockViolation(): void {
    const refineryPresence = this.injector.get(RefineryPresenceService);
    refineryPresence.forceReadOnly();
  }
}
